<script setup lang="ts">
/*
 * The text search of the price list's catalog modal (24.09.2026) — a filter on the series already
 * shown, on top of the mask's filters, with suggestions taken from those same series.
 *
 * Laid out like SearchInput (UPopover + UInput + suggestion list, same classes) so both search fields
 * look alike; deliberately NOT SearchInput itself: that one asks the server, navigates to the result
 * page and owns a global shortcut, and none of that belongs here. Nothing is fetched — the parent
 * hands in the suggestions and decides what the text filters.
 */
import { ut_getText } from '@base/utils/utils'

const p = defineProps<{
  modelValue: string
  suggestions: string[]
}>()

const emit = defineEmits<{
  'update:modelValue': [string]
}>()

const focused: Ref<boolean> = ref(false)

// Open while the field is in use and there is something to suggest.
const open = computed({
  get: (): boolean => focused.value && !!p.modelValue && p.suggestions.length > 0,
  set: (value: boolean): void => { if (!value) { focused.value = false } },
})

function pick(suggestion: string): void {
  emit('update:modelValue', suggestion)
  focused.value = false
}
</script>

<template>
  <UPopover v-model:open="open" :dismissible="true">
    <template #anchor>
      <UInput
        :model-value="modelValue"
        name="catalog-search"
        class="md:w-75"
        :placeholder="ut_getText('def_search_txt_field')"
        @focus="focused = true"
        @update:model-value="emit('update:modelValue', String($event)); focused = true"
      >
        <template #trailing>
          <UButton icon="i-wilo-magnifying-glass-solid-full" variant="ghost" color="tertiary" :disabled="modelValue.length === 0" @click="focused = false" />
        </template>
      </UInput>
    </template>

    <template #content>
      <div class="p-1">
        <ul>
          <li
            v-for="suggestion in suggestions"
            :key="suggestion"
            class="hover:bg-gray-50 p-4 cursor-pointer"
            @click="pick(suggestion)"
            v-html="suggestion"
          />
        </ul>
      </div>
    </template>
  </UPopover>
</template>
